import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import clsx from 'clsx';
import { config } from '../lib/config';
import { supabase } from '../lib/supabase';

type StatusTone = 'info' | 'success' | 'error' | 'warning';

type StatusMessage = {
  tone: StatusTone;
  text: string;
};

type CmsSeo = {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImageUrl?: string;
  noindex?: boolean;
};

type CmsPageType = 'home' | 'landing' | 'standard';

type CmsLandingPageData = {
  templateKey?: string;
  themeKey?: string;
  audienceLabel?: string;
  heroMode?: string;
  heroSceneOutputId?: string;
  heroImageUrl?: string;
  heroImageAlt?: string;
  ogImageUrl?: string;
  stickyCtaLabel?: string;
};

type CmsSection = {
  id: string;
  type: string;
  hidden?: boolean;
  props?: Record<string, unknown>;
};

type CmsVersion = {
  id: string;
  page_id: string;
  version_number: number;
  status: 'draft' | 'published' | 'archived';
  title: string;
  seo: CmsSeo;
  sections: CmsSection[];
  page_data: Record<string, unknown> & {
    landing?: CmsLandingPageData;
  };
  updated_at: string;
  published_at?: string | null;
};

type CmsPageRecord = {
  id: string;
  route: string;
  slug: string;
  title: string;
  page_type: CmsPageType;
  draft_version_id: string | null;
  published_version_id: string | null;
  updated_at: string;
  draft?: CmsVersion | null;
  published?: CmsVersion | null;
};

type CmsGlobals = Record<string, unknown>;

type CmsRevision = {
  id: string;
  version_number: number;
  status: string;
  title: string;
  created_at: string;
  updated_at: string;
  published_at?: string | null;
};

type CmsMediaAsset = {
  id: string;
  bucket: string;
  path: string;
  title: string;
  alt_text: string;
  publicUrl: string;
  usageCount: number;
  mime_type?: string | null;
  focal_x?: number | null;
  focal_y?: number | null;
  metadata?: Record<string, unknown>;
};

type CmsGetPageResponse = {
  page: CmsPageRecord;
  globals: { key: string; value: CmsGlobals; updated_at: string } | null;
};

type CmsActionResponse<T> = T & { ok?: boolean; error?: string };
type CmsPreviewMode = 'live' | 'outline';
type CmsViewportMode = 'desktop' | 'tablet' | 'mobile';
type CmsUtilityTab = 'pages' | 'settings' | 'media';
type CmsEditorMode = 'content' | 'page' | 'history';
type DraftSaveState = 'saved' | 'saving' | 'unsaved' | 'error';
type PreviewSyncState = 'idle' | 'updating' | 'error';
type AssetTarget =
  | { kind: 'section'; field: string; altField?: string }
  | { kind: 'landing'; field: 'heroImageUrl' | 'ogImageUrl'; altField?: 'heroImageAlt' };

type SectionTemplate = {
  type: string;
  label: string;
  defaultSection: CmsSection;
};

const SECTION_LIBRARY: SectionTemplate[] = [
  {
    type: 'hero',
    label: 'Hero',
    defaultSection: {
      id: 'hero',
      type: 'hero',
      props: {
        eyebrow: 'Eyebrow',
        title: 'Hero title',
        body: 'Introduce the page in one strong paragraph.',
        primaryLabel: 'Primary action',
        primaryHref: 'https://rvlry.app',
        secondaryLabel: 'Secondary action',
        secondaryHref: 'https://rvlryapp.com/app/support.html',
        theme: 'sunrise',
      },
    },
  },
  {
    type: 'split_image_text',
    label: 'Split Image + Text',
    defaultSection: {
      id: 'split-image',
      type: 'split_image_text',
      props: {
        eyebrow: 'Context',
        title: 'Section title',
        body: 'Explain the image or concept.',
        imageUrl: '',
        imageAlt: '',
        reverse: false,
      },
    },
  },
  {
    type: 'feature_grid',
    label: 'Feature Grid',
    defaultSection: {
      id: 'feature-grid',
      type: 'feature_grid',
      props: {
        title: 'Feature grid title',
        body: 'Short introduction.',
        items: [
          { title: 'First feature', body: 'What matters here.' },
          { title: 'Second feature', body: 'Why it matters.' },
          { title: 'Third feature', body: 'What people get from it.' },
        ],
      },
    },
  },
  {
    type: 'stats_strip',
    label: 'Stats Strip',
    defaultSection: {
      id: 'stats-strip',
      type: 'stats_strip',
      props: {
        title: 'Stats title',
        items: [
          { label: 'Label', value: 'Value' },
          { label: 'Label', value: 'Value' },
          { label: 'Label', value: 'Value' },
        ],
      },
    },
  },
  {
    type: 'cta_band',
    label: 'CTA Band',
    defaultSection: {
      id: 'cta-band',
      type: 'cta_band',
      props: {
        title: 'Ready to move?',
        body: 'Call people toward the next step.',
        primaryLabel: 'Primary CTA',
        primaryHref: 'https://rvlry.app',
        secondaryLabel: 'Secondary CTA',
        secondaryHref: 'https://rvlryapp.com/app/support.html',
      },
    },
  },
  {
    type: 'testimonial',
    label: 'Testimonial',
    defaultSection: {
      id: 'testimonial',
      type: 'testimonial',
      props: {
        quote: 'A sharp quote that adds social proof.',
        name: 'Name',
        role: 'Role',
      },
    },
  },
  {
    type: 'faq',
    label: 'FAQ',
    defaultSection: {
      id: 'faq',
      type: 'faq',
      props: {
        title: 'Frequently asked questions',
        items: [
          { question: 'Question one?', answer: 'Answer one.' },
          { question: 'Question two?', answer: 'Answer two.' },
        ],
      },
    },
  },
  {
    type: 'rich_text',
    label: 'Rich Text / Legal',
    defaultSection: {
      id: 'rich-text',
      type: 'rich_text',
      props: {
        title: 'Rich text title',
        html: '<p>Paste or author rich HTML content here.</p>',
      },
    },
  },
  {
    type: 'support_cards',
    label: 'Support Cards',
    defaultSection: {
      id: 'support-cards',
      type: 'support_cards',
      props: {
        title: 'Support cards',
        cards: [
          { title: 'Card title', body: 'Helpful context.', label: 'Action label', href: 'mailto:support@rvlryapp.com' },
        ],
      },
    },
  },
  {
    type: 'app_download',
    label: 'App Download',
    defaultSection: {
      id: 'app-download',
      type: 'app_download',
      props: {
        title: 'Download RVLRY',
        body: 'Point people to the right install path.',
        iosLabel: 'Open App',
        iosHref: 'https://rvlry.app',
        androidLabel: 'Play in browser',
        androidHref: 'https://play.rvlryapp.com',
        secondaryLabel: 'Support',
        secondaryHref: 'mailto:support@rvlryapp.com',
      },
    },
  },
  {
    type: 'link_list',
    label: 'Link List',
    defaultSection: {
      id: 'link-list',
      type: 'link_list',
      props: {
        title: 'Links',
        links: [
          { label: 'Link label', href: 'https://rvlryapp.com', description: 'Optional description' },
        ],
      },
    },
  },
  {
    type: 'media_gallery',
    label: 'Media Gallery',
    defaultSection: {
      id: 'media-gallery',
      type: 'media_gallery',
      props: {
        title: 'Media gallery',
        items: [
          { imageUrl: '', alt: '', caption: '' },
        ],
      },
    },
  },
];

const LANDING_REQUIRED_SECTION_IDS = [
  'landing-hero',
  'landing-benefits',
  'landing-proof',
  'landing-story-1',
  'landing-story-2',
  'landing-faq',
  'landing-cta',
];

const LANDING_HERO_SCENE_OPTIONS = [
  { value: 'rivals-community-desktop', label: 'Rivals Community' },
  { value: 'venue-map-desktop', label: 'Venue Map' },
  { value: 'lobby-team-builder-desktop', label: 'Lobby Team Builder' },
  { value: 'qr-join-desktop', label: 'QR Join' },
];

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function stableStringify(value: unknown) {
  return JSON.stringify(value, null, 2);
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

async function cmsFetch<T>(session: Session, payload: Record<string, unknown>): Promise<CmsActionResponse<T>> {
  const url = `${config.supabaseUrl}/functions/v1/cms-console`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: config.supabaseAnonKey,
      authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as CmsActionResponse<T>;
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  if (body.error) throw new Error(body.error);
  return body;
}

function statusPill(msg: StatusMessage | null) {
  if (!msg?.text) return null;
  return <div className={clsx('admin-status', msg.tone)}>{msg.text}</div>;
}

function shouldShowStatusBanner(msg: StatusMessage | null) {
  if (!msg?.text) return false;
  if (msg.tone === 'error' || msg.tone === 'warning') return true;
  return /Published|created|deleted|uploaded|restored|saved\.$|saved\.$/i.test(msg.text);
}

function findTemplate(type: string) {
  return SECTION_LIBRARY.find((item) => item.type === type);
}

function sectionSummary(section: CmsSection) {
  const props = section.props ?? {};
  const primary = textValue(props.title)
    || textValue(props.question)
    || textValue(props.quote)
    || textValue(props.label)
    || textValue(props.eyebrow);
  const secondary = textValue(props.body)
    || textValue(props.answer)
    || textValue(props.description)
    || textValue(props.role)
    || textValue(props.html).replace(/<[^>]+>/g, ' ');
  return {
    primary: primary || 'No headline yet',
    secondary: secondary || 'Add copy, media, links, or structure settings.',
  };
}

function publicPageHref(route: string) {
  const normalized = route === '/' ? '' : route;
  return `https://rvlryapp.com${normalized}`;
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function textValue(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function boolValue(value: unknown) {
  return Boolean(value);
}

function arrayValue<T = Record<string, unknown>>(value: unknown) {
  return Array.isArray(value) ? (value as T[]) : [];
}

function pageTypeLabel(pageType: CmsPageType) {
  switch (pageType) {
    case 'home':
      return 'Home';
    case 'landing':
      return 'Landing';
    default:
      return 'Standard';
  }
}

function landingData(version: CmsVersion | null | undefined): CmsLandingPageData {
  return ((version?.page_data?.landing as CmsLandingPageData | undefined) ?? {});
}

function isLandingRequiredSection(page: CmsPageRecord | null, sectionId: string) {
  return page?.page_type === 'landing' && LANDING_REQUIRED_SECTION_IDS.includes(sectionId);
}

function OutlinePagePreview({ page, globals }: { page: CmsPageRecord | null; globals: CmsGlobals }) {
  const version = page?.draft ?? page?.published ?? null;
  if (!page || !version) {
    return (
      <div className="cms-preview-empty">
        <strong>Select a page</strong>
        <span>Choose a page from the navigator to inspect or edit it.</span>
      </div>
    );
  }

  return (
    <div className="cms-preview-shell">
      <div className="cms-preview-topbar">
        <div className="cms-preview-brand">{String(globals.siteName || 'RVLRY')}</div>
        <div className="cms-preview-route">{page.route}</div>
      </div>
      {page.route === '/join/' ? (
        <div className="cms-preview-join">
          <div className="cms-preview-chip">{textValue(version.page_data?.joinShell && (version.page_data.joinShell as Record<string, unknown>).eyebrow)}</div>
          <h1>{textValue(version.page_data?.joinShell && (version.page_data.joinShell as Record<string, unknown>).title) || 'Join on RVLRY'}</h1>
          <p>{textValue(version.page_data?.joinShell && (version.page_data.joinShell as Record<string, unknown>).body)}</p>
        </div>
      ) : null}
      <div className="cms-preview-sections">
        {(version.sections || []).filter((section) => !section.hidden).map((section) => {
          const props = section.props ?? {};
          switch (section.type) {
            case 'hero':
              return (
                <section className="cms-preview-hero" key={section.id}>
                  {textValue(props.eyebrow) ? <div className="cms-preview-chip">{textValue(props.eyebrow)}</div> : null}
                  <h1>{textValue(props.title)}</h1>
                  <p>{textValue(props.body)}</p>
                </section>
              );
            case 'feature_grid':
            case 'support_cards':
              return (
                <section className="cms-preview-panel" key={section.id}>
                  <h2>{textValue(props.title)}</h2>
                  <div className="cms-preview-grid">
                    {arrayValue<Record<string, unknown>>(props.items ?? props.cards).map((item, index) => (
                      <article className="cms-preview-card" key={`${section.id}-${index}`}>
                        <strong>{textValue(item.title)}</strong>
                        <p>{textValue(item.body)}</p>
                      </article>
                    ))}
                  </div>
                </section>
              );
            case 'stats_strip':
              return (
                <section className="cms-preview-panel" key={section.id}>
                  <h2>{textValue(props.title)}</h2>
                  <div className="cms-preview-grid cms-preview-grid--stats">
                    {arrayValue<Record<string, unknown>>(props.items).map((item, index) => (
                      <article className="cms-preview-card" key={`${section.id}-${index}`}>
                        <strong>{textValue(item.value)}</strong>
                        <span>{textValue(item.label)}</span>
                      </article>
                    ))}
                  </div>
                </section>
              );
            case 'rich_text':
              return (
                <section className="cms-preview-panel" key={section.id}>
                  <h2>{textValue(props.title)}</h2>
                  <div dangerouslySetInnerHTML={{ __html: textValue(props.html) }} />
                </section>
              );
            case 'link_list':
              return (
                <section className="cms-preview-panel" key={section.id}>
                  <h2>{textValue(props.title)}</h2>
                  <div className="cms-preview-list">
                    {arrayValue<Record<string, unknown>>(props.links).map((item, index) => (
                      <a href={textValue(item.href)} key={`${section.id}-${index}`}>
                        <strong>{textValue(item.label)}</strong>
                        <span>{textValue(item.description)}</span>
                      </a>
                    ))}
                  </div>
                </section>
              );
            default:
              return (
                <section className="cms-preview-panel" key={section.id}>
                  <h2>{findTemplate(section.type)?.label || section.type}</h2>
                  <pre className="cms-preview-code">{stableStringify(section.props)}</pre>
                </section>
              );
          }
        })}
      </div>
    </div>
  );
}

function LivePagePreview({
  previewUrl,
  viewport,
  previewBusy,
  page,
  onRefresh,
  statusText,
}: {
  previewUrl: string;
  viewport: CmsViewportMode;
  previewBusy: boolean;
  page: CmsPageRecord | null;
  onRefresh: () => void;
  statusText: string;
}) {
  if (!page) {
    return (
      <div className="cms-preview-empty cms-live-preview-empty">
        <strong>Select a page</strong>
        <span>Choose a page from the drawer to load a live preview.</span>
      </div>
    );
  }

  return (
    <div className="cms-live-preview-shell">
      <div className="cms-live-preview-meta">
        <div>
          <strong>{page.route}</strong>
          <span>{statusText}</span>
        </div>
        <button className="admin-btn small" type="button" onClick={onRefresh} disabled={previewBusy}>
          {previewBusy ? 'Refreshing…' : 'Refresh preview'}
        </button>
      </div>
      <div className={`cms-live-preview-stage ${viewport === 'tablet' ? 'tablet' : viewport === 'mobile' ? 'mobile' : 'desktop'}`}>
        {previewUrl ? (
          <iframe
            key={previewUrl}
            className="cms-live-preview-frame"
            src={previewUrl}
            title={`${page.route} live preview`}
          />
        ) : (
          <div className="cms-preview-empty cms-live-preview-empty">
            <strong>Preview unavailable</strong>
            <span>Create a preview token to load the rendered page.</span>
          </div>
        )}
      </div>
    </div>
  );
}

function JsonTextarea({
  label,
  value,
  onChange,
  help,
}: {
  label: string;
  value: unknown;
  onChange: (next: unknown) => void;
  help?: string;
}) {
  const [text, setText] = useState(() => stableStringify(value ?? []));
  const [error, setError] = useState('');

  useEffect(() => {
    setText(stableStringify(value ?? []));
    setError('');
  }, [value]);

  return (
    <label className="admin-label">
      {label}
      <textarea
        className="admin-input cms-json-textarea"
        value={text}
        spellCheck={false}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          try {
            onChange(JSON.parse(next));
            setError('');
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      />
      {help ? <div className="admin-help">{help}</div> : null}
      {error ? <div className="admin-help" style={{ color: '#b42318' }}>{error}</div> : null}
    </label>
  );
}

type CollectionFieldConfig = {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'checkbox';
  placeholder?: string;
};

function CollectionRowsEditor({
  label,
  items,
  onChange,
  fields,
  addLabel,
}: {
  label: string;
  items: Record<string, unknown>[];
  onChange: (next: Record<string, unknown>[]) => void;
  fields: CollectionFieldConfig[];
  addLabel: string;
}) {
  const updateItem = (index: number, key: string, value: unknown) => {
    const next = items.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item));
    onChange(next);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, itemIndex) => itemIndex !== index));
  };

  const addItem = () => {
    const nextItem = fields.reduce<Record<string, unknown>>((acc, field) => {
      acc[field.key] = field.type === 'checkbox' ? false : '';
      return acc;
    }, {});
    onChange([...items, nextItem]);
  };

  return (
    <div className="cms-collection-editor">
      <div className="cms-collection-head">
        <div>
          <div className="admin-section-title">{label}</div>
          <div className="admin-muted">Each item is rendered as its own card or repeated content block.</div>
        </div>
        <button className="admin-btn small" type="button" onClick={addItem}>
          {addLabel}
        </button>
      </div>
      <div className="cms-collection-list">
        {items.map((item, index) => (
          <div className="cms-collection-item" key={`${label}-${index}`}>
            <div className="cms-collection-item-head">
              <div>
                <div className="cms-collection-item-kicker">Item {index + 1}</div>
                <strong>{textValue(item.title) || textValue(item.label) || textValue(item.question) || `Item ${index + 1}`}</strong>
              </div>
            </div>
            <div className="cms-collection-grid">
              {fields.map((field) => (
                <label className={clsx('admin-label', field.type === 'textarea' && 'cms-collection-field-wide')} key={`${index}-${field.key}`}>
                  {field.label}
                  {field.type === 'textarea' ? (
                    <textarea
                      className="admin-input"
                      rows={6}
                      value={textValue(item[field.key])}
                      placeholder={field.placeholder}
                      onChange={(event) => updateItem(index, field.key, event.target.value)}
                    />
                  ) : field.type === 'checkbox' ? (
                    <input
                      type="checkbox"
                      checked={Boolean(item[field.key])}
                      onChange={(event) => updateItem(index, field.key, event.target.checked)}
                    />
                  ) : (
                    <input
                      className="admin-input"
                      value={textValue(item[field.key])}
                      placeholder={field.placeholder}
                      onChange={(event) => updateItem(index, field.key, event.target.value)}
                    />
                  )}
                </label>
              ))}
            </div>
            <div className="cms-collection-item-actions">
              <button className="admin-btn small ghost danger cms-collection-remove" type="button" onClick={() => removeItem(index)}>
                Delete item
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionEditor({
  section,
  updateProps,
  setHidden,
  pickMedia,
}: {
  section: CmsSection;
  updateProps: (next: Record<string, unknown>) => void;
  setHidden: (next: boolean) => void;
  pickMedia: (field: string, altField?: string) => void;
}) {
  const props = clone(section.props ?? {});
  const setField = (key: string, value: unknown) => updateProps({ ...props, [key]: value });
  const collectionConfig = (() => {
    switch (section.type) {
      case 'feature_grid':
        return {
          field: 'items',
          label: 'Feature cards',
          addLabel: 'Add feature',
          fields: [
            { key: 'accent', label: 'Eyebrow' },
            { key: 'title', label: 'Title' },
            { key: 'body', label: 'Body', type: 'textarea' as const },
          ],
        };
      case 'support_cards':
        return {
          field: 'cards',
          label: 'Cards',
          addLabel: 'Add card',
          fields: [
            { key: 'title', label: 'Title' },
            { key: 'body', label: 'Body', type: 'textarea' as const },
            { key: 'label', label: 'Button label' },
            { key: 'href', label: 'Button link' },
          ],
        };
      case 'stats_strip':
        return {
          field: 'items',
          label: 'Stats',
          addLabel: 'Add stat',
          fields: [
            { key: 'value', label: 'Value' },
            { key: 'label', label: 'Label' },
          ],
        };
      case 'faq':
        return {
          field: 'items',
          label: 'Questions',
          addLabel: 'Add question',
          fields: [
            { key: 'question', label: 'Question' },
            { key: 'answer', label: 'Answer', type: 'textarea' as const },
          ],
        };
      case 'link_list':
        return {
          field: 'links',
          label: 'Links',
          addLabel: 'Add link',
          fields: [
            { key: 'label', label: 'Label' },
            { key: 'href', label: 'Link' },
            { key: 'description', label: 'Description', type: 'textarea' as const },
          ],
        };
      case 'media_gallery':
        return {
          field: 'items',
          label: 'Media items',
          addLabel: 'Add media item',
          fields: [
            { key: 'imageUrl', label: 'Image URL' },
            { key: 'alt', label: 'Alt text' },
            { key: 'caption', label: 'Caption', type: 'textarea' as const },
          ],
        };
      default:
        return null;
    }
  })();

  return (
    <div className="cms-inspector-block">
      <div className="cms-editor-form-head">
        <div>
          <div className="cms-editor-form-kicker">{findTemplate(section.type)?.label || section.type}</div>
          <h3 className="cms-editor-form-title">{section.id}</h3>
        </div>
      </div>
      <div className="cms-editor-form-section">
        <div className="cms-editor-form-section-head">
          <div className="cms-editor-form-section-kicker">Core content</div>
          <h4>What visitors read first</h4>
        </div>
        <label className="admin-label">
          Section hidden
          <input
            type="checkbox"
            checked={Boolean(section.hidden)}
            onChange={(event) => setHidden(event.target.checked)}
          />
        </label>
      {['hero', 'split_image_text', 'cta_band', 'testimonial', 'app_download'].includes(section.type) ? (
        <>
          {'eyebrow' in props ? (
            <label className="admin-label">
              Eyebrow
              <input className="admin-input" value={textValue(props.eyebrow)} onChange={(e) => setField('eyebrow', e.target.value)} />
            </label>
          ) : null}
          {'title' in props ? (
            <label className="admin-label">
              Title
              <input className="admin-input" value={textValue(props.title)} onChange={(e) => setField('title', e.target.value)} />
            </label>
          ) : null}
          {'body' in props ? (
            <label className="admin-label">
              Body
              <textarea className="admin-input" rows={5} value={textValue(props.body)} onChange={(e) => setField('body', e.target.value)} />
            </label>
          ) : null}
        </>
      ) : null}
      </div>

      {section.type === 'split_image_text' ? (
        <div className="cms-editor-form-section">
          <div className="cms-editor-form-section-head">
            <div className="cms-editor-form-section-kicker">Layout and media</div>
            <h4>Art direction</h4>
          </div>
          <label className="admin-label">
            Image URL
            <div className="cms-inline-field">
              <input className="admin-input" value={textValue(props.imageUrl)} onChange={(e) => setField('imageUrl', e.target.value)} />
              <button className="admin-btn" type="button" onClick={() => pickMedia('imageUrl', 'imageAlt')}>Pick</button>
            </div>
          </label>
          <label className="admin-label">
            Image alt
            <input className="admin-input" value={textValue(props.imageAlt)} onChange={(e) => setField('imageAlt', e.target.value)} />
          </label>
          <label className="admin-label">
            Reverse layout
            <input type="checkbox" checked={boolValue(props.reverse)} onChange={(e) => setField('reverse', e.target.checked)} />
          </label>
        </div>
      ) : null}

      {section.type === 'hero' ? (
        <div className="cms-editor-form-section">
          <div className="cms-editor-form-section-head">
            <div className="cms-editor-form-section-kicker">Layout and media</div>
            <h4>Hero presentation</h4>
          </div>
          <label className="admin-label">
            Hero layout
            <select className="admin-select" value={textValue(props.heroLayout) || 'centered'} onChange={(e) => setField('heroLayout', e.target.value)}>
              <option value="centered">Centered copy</option>
              <option value="split">Text left + image right</option>
            </select>
          </label>
          <label className="admin-label">
            Hero image URL
            <div className="cms-inline-field">
              <input className="admin-input" value={textValue(props.sceneImageUrl)} onChange={(e) => setField('sceneImageUrl', e.target.value)} />
              <button className="admin-btn" type="button" onClick={() => pickMedia('sceneImageUrl', 'sceneImageAlt')}>Pick</button>
            </div>
          </label>
          <label className="admin-label">
            Hero image alt
            <input className="admin-input" value={textValue(props.sceneImageAlt)} onChange={(e) => setField('sceneImageAlt', e.target.value)} />
          </label>
        </div>
      ) : null}

      {section.type === 'hero' || section.type === 'cta_band' ? (
        <div className="cms-editor-form-section">
          <div className="cms-editor-form-section-head">
            <div className="cms-editor-form-section-kicker">Actions</div>
            <h4>Buttons and destinations</h4>
          </div>
          <label className="admin-label">
            Primary label
            <input className="admin-input" value={textValue(props.primaryLabel)} onChange={(e) => setField('primaryLabel', e.target.value)} />
          </label>
          <label className="admin-label">
            Primary href
            <input className="admin-input" value={textValue(props.primaryHref)} onChange={(e) => setField('primaryHref', e.target.value)} />
          </label>
          <label className="admin-label">
            Secondary label
            <input className="admin-input" value={textValue(props.secondaryLabel)} onChange={(e) => setField('secondaryLabel', e.target.value)} />
          </label>
          <label className="admin-label">
            Secondary href
            <input className="admin-input" value={textValue(props.secondaryHref)} onChange={(e) => setField('secondaryHref', e.target.value)} />
          </label>
        </div>
      ) : null}

      {section.type === 'rich_text' ? (
        <div className="cms-editor-form-section">
          <div className="cms-editor-form-section-head">
            <div className="cms-editor-form-section-kicker">Rich text</div>
            <h4>Formatted content</h4>
          </div>
          <label className="admin-label">
            HTML
            <textarea className="admin-input cms-rich-textarea" rows={12} spellCheck={false} value={textValue(props.html)} onChange={(e) => setField('html', e.target.value)} />
          </label>
        </div>
      ) : null}

      {section.type === 'testimonial' ? (
        <div className="cms-editor-form-section">
          <div className="cms-editor-form-section-head">
            <div className="cms-editor-form-section-kicker">Social proof</div>
            <h4>Quote details</h4>
          </div>
          <label className="admin-label">
            Quote
            <textarea className="admin-input" rows={5} value={textValue(props.quote)} onChange={(e) => setField('quote', e.target.value)} />
          </label>
          <label className="admin-label">
            Name
            <input className="admin-input" value={textValue(props.name)} onChange={(e) => setField('name', e.target.value)} />
          </label>
          <label className="admin-label">
            Role
            <input className="admin-input" value={textValue(props.role)} onChange={(e) => setField('role', e.target.value)} />
          </label>
        </div>
      ) : null}

      {section.type === 'app_download' ? (
        <div className="cms-editor-form-section">
          <div className="cms-editor-form-section-head">
            <div className="cms-editor-form-section-kicker">Actions</div>
            <h4>Store and support paths</h4>
          </div>
          <label className="admin-label">
            Primary label
            <input className="admin-input" value={textValue(props.iosLabel)} onChange={(e) => setField('iosLabel', e.target.value)} />
          </label>
          <label className="admin-label">
            Primary href
            <input className="admin-input" value={textValue(props.iosHref)} onChange={(e) => setField('iosHref', e.target.value)} />
          </label>
          <label className="admin-label">
            Secondary label
            <input className="admin-input" value={textValue(props.androidLabel)} onChange={(e) => setField('androidLabel', e.target.value)} />
          </label>
          <label className="admin-label">
            Secondary href
            <input className="admin-input" value={textValue(props.androidHref)} onChange={(e) => setField('androidHref', e.target.value)} />
          </label>
          <label className="admin-label">
            Tertiary label
            <input className="admin-input" value={textValue(props.secondaryLabel)} onChange={(e) => setField('secondaryLabel', e.target.value)} />
          </label>
          <label className="admin-label">
            Tertiary href
            <input className="admin-input" value={textValue(props.secondaryHref)} onChange={(e) => setField('secondaryHref', e.target.value)} />
          </label>
        </div>
      ) : null}

      {collectionConfig ? (
        <div className="cms-editor-form-section">
          <div className="cms-editor-form-section-head">
            <div className="cms-editor-form-section-kicker">Collections</div>
            <h4>Repeatable content</h4>
          </div>
          <CollectionRowsEditor
            label={collectionConfig.label}
            addLabel={collectionConfig.addLabel}
            items={arrayValue<Record<string, unknown>>(props[collectionConfig.field])}
            onChange={(next) => setField(collectionConfig.field, next)}
            fields={collectionConfig.fields}
          />
        </div>
      ) : null}

      {section.type === 'feature_grid' || section.type === 'stats_strip' || section.type === 'faq' || section.type === 'support_cards' || section.type === 'link_list' || section.type === 'media_gallery' ? (
        <div className="cms-editor-form-section">
          <div className="cms-editor-form-section-head">
            <div className="cms-editor-form-section-kicker">Section settings</div>
            <h4>Header copy</h4>
          </div>
          <label className="admin-label">
            Section title
            <input className="admin-input" value={textValue(props.title)} onChange={(e) => setField('title', e.target.value)} />
          </label>
          {'body' in props ? (
            <label className="admin-label">
              Section body
              <textarea className="admin-input" rows={5} value={textValue(props.body)} onChange={(e) => setField('body', e.target.value)} />
            </label>
          ) : null}
        </div>
      ) : null}

      <details className="admin-details cms-advanced-details">
        <summary>Advanced</summary>
        <pre className="admin-code">{stableStringify(props)}</pre>
      </details>
    </div>
  );
}

export default function CmsWorkspace({ session }: { session: Session }) {
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [pages, setPages] = useState<CmsPageRecord[]>([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [page, setPage] = useState<CmsPageRecord | null>(null);
  const [globalsValue, setGlobalsValue] = useState<CmsGlobals>({});
  const [baselineDraft, setBaselineDraft] = useState<CmsVersion | null>(null);
  const [draft, setDraft] = useState<CmsVersion | null>(null);
  const [revisions, setRevisions] = useState<CmsRevision[]>([]);
  const [media, setMedia] = useState<CmsMediaAsset[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [savingGlobals, setSavingGlobals] = useState(false);
  const [draftSaveState, setDraftSaveState] = useState<DraftSaveState>('saved');
  const [previewSyncState, setPreviewSyncState] = useState<PreviewSyncState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState('');
  const [mediaSearch, setMediaSearch] = useState('');
  const [assetTarget, setAssetTarget] = useState<AssetTarget | null>(null);
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [leftDrawerTab, setLeftDrawerTab] = useState<CmsUtilityTab>('pages');
  const [editorMode, setEditorMode] = useState<CmsEditorMode>('content');
  const [previewMode, setPreviewMode] = useState<CmsPreviewMode>('live');
  const [viewport, setViewport] = useState<CmsViewportMode>('desktop');
  const [canvasFocus, setCanvasFocus] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageRoute, setNewPageRoute] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [newPageType, setNewPageType] = useState<CmsPageType>('standard');
  const [newPageSourceId, setNewPageSourceId] = useState('');
  const [sectionSearch, setSectionSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const autosaveTimerRef = useRef<number | null>(null);
  const previewRefreshTimerRef = useRef<number | null>(null);
  const savePromiseRef = useRef<Promise<boolean> | null>(null);
  const pageRef = useRef<CmsPageRecord | null>(null);
  const draftRef = useRef<CmsVersion | null>(null);
  const baselineDraftRef = useRef<CmsVersion | null>(null);
  const dirtyRef = useRef(false);
  const pendingPreviewRefreshRef = useRef(false);
  const draftUpdatedAtRef = useRef('');

  const activeVersion = useMemo(
    () => draft ?? page?.published ?? null,
    [draft, page?.published],
  );
  const dirty = useMemo(() => stableStringify(baselineDraft) !== stableStringify(draft), [baselineDraft, draft]);
  const selectedSection = useMemo(
    () => activeVersion?.sections.find((section) => section.id === selectedSectionId) ?? activeVersion?.sections[0] ?? null,
    [activeVersion, selectedSectionId],
  );
  const heroSection = useMemo(
    () => activeVersion?.sections.find((section) => section.type === 'hero') ?? null,
    [activeVersion],
  );
  const currentVersionId = draft?.id ?? page?.published?.id ?? '';
  const filteredSections = useMemo(() => {
    const query = sectionSearch.trim().toLowerCase();
    const sections = activeVersion?.sections ?? [];
    if (!query) return sections;
    return sections.filter((section) => {
      const summary = sectionSummary(section);
      const label = findTemplate(section.type)?.label || section.type;
      return [label, section.id, summary.primary, summary.secondary]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [activeVersion?.sections, sectionSearch]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    baselineDraftRef.current = baselineDraft;
  }, [baselineDraft]);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  useEffect(() => {
    if (!draft) {
      setDraftSaveState('saved');
      setLastSavedAt('');
      return;
    }
    if (saveBusy || publishBusy) {
      setDraftSaveState('saving');
      return;
    }
    setDraftSaveState(dirty ? 'unsaved' : 'saved');
  }, [draft, dirty, saveBusy, publishBusy]);

  const refreshEmbeddedPreview = useCallback(async () => {
    if (!page) {
      setPreviewUrl('');
      return '';
    }
    setPreviewBusy(true);
    setPreviewSyncState('updating');
    try {
      const result = await cmsFetch<{ previewUrl: string }>(session, { action: 'cms:preview:create', pageId: page.id });
      const previewHref = result.previewUrl.includes('?')
        ? `${result.previewUrl}&embed=1&t=${Date.now()}`
        : `${result.previewUrl}?embed=1&t=${Date.now()}`;
      setPreviewUrl(previewHref);
      pendingPreviewRefreshRef.current = false;
      setPreviewSyncState('idle');
      return previewHref;
    } catch (error) {
      setStatus({ tone: 'warning', text: (error as Error).message || 'Preview may be behind; retry.' });
      setPreviewSyncState('error');
      return '';
    } finally {
      setPreviewBusy(false);
    }
  }, [page, session]);

  const loadPages = useCallback(async () => {
    const result = await cmsFetch<{ pages: CmsPageRecord[] }>(session, { action: 'cms:pages:list' });
    setPages(result.pages ?? []);
    if (!selectedPageId && result.pages?.[0]?.id) {
      setSelectedPageId(result.pages[0].id);
    }
  }, [session, selectedPageId]);

  const loadMedia = useCallback(async () => {
    const result = await cmsFetch<{ assets: CmsMediaAsset[] }>(session, { action: 'cms:media:list' });
    setMedia(result.assets ?? []);
  }, [session]);

  const loadPage = useCallback(async (pageId: string) => {
    setLoading(true);
    try {
      const [pageResult, revisionsResult] = await Promise.all([
        cmsFetch<CmsGetPageResponse>(session, { action: 'cms:page:get', pageId }),
        cmsFetch<{ revisions: CmsRevision[] }>(session, { action: 'cms:revisions:list', pageId }),
      ]);
      setPage(pageResult.page);
      setGlobalsValue((pageResult.globals?.value ?? {}) as CmsGlobals);
      const currentDraft = pageResult.page.draft ?? null;
      setBaselineDraft(currentDraft ? clone(currentDraft) : null);
      setDraft(currentDraft ? clone(currentDraft) : null);
      setLastSavedAt(currentDraft?.updated_at ?? '');
      draftUpdatedAtRef.current = currentDraft?.updated_at ?? '';
      setPreviewSyncState('idle');
      setRevisions(revisionsResult.revisions ?? []);
      setSelectedSectionId(currentDraft?.sections?.[0]?.id ?? pageResult.page.published?.sections?.[0]?.id ?? '');
      setStatus(null);
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to load page.' });
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    loadPages().catch((error) => setStatus({ tone: 'error', text: (error as Error).message }));
    loadMedia().catch((error) => setStatus({ tone: 'error', text: (error as Error).message }));
  }, [loadPages, loadMedia]);

  useEffect(() => {
    if (!selectedPageId) return;
    loadPage(selectedPageId).catch((error) => setStatus({ tone: 'error', text: (error as Error).message }));
  }, [selectedPageId, loadPage]);

  useEffect(() => {
    if (!currentVersionId) {
      setPreviewUrl('');
      return;
    }
    refreshEmbeddedPreview().catch((error) => setStatus({ tone: 'error', text: (error as Error).message }));
  }, [currentVersionId, refreshEmbeddedPreview]);

  const updateDraft = useCallback((updater: (current: CmsVersion) => CmsVersion) => {
    setDraft((current) => (current ? updater(clone(current)) : current));
  }, []);

  const updateSelectedSection = useCallback((updater: (section: CmsSection) => CmsSection) => {
    updateDraft((current) => ({
      ...current,
      sections: current.sections.map((section) => (
        section.id === selectedSectionId ? updater(clone(section)) : section
      )),
    }));
  }, [selectedSectionId, updateDraft]);

  const updateLandingData = useCallback((updater: (current: CmsLandingPageData) => CmsLandingPageData) => {
    updateDraft((current) => ({
      ...current,
      page_data: {
        ...current.page_data,
        landing: updater(clone(landingData(current))),
      },
    }));
  }, [updateDraft]);

  const resetNewPageForm = useCallback(() => {
    setNewPageTitle('');
    setNewPageRoute('');
    setNewPageSlug('');
    setNewPageType('standard');
    setNewPageSourceId('');
    setNewPageOpen(false);
  }, []);

  const ensureDraft = useCallback(async () => {
    if (!page) return false;
    setLoading(true);
    try {
      await cmsFetch(session, { action: 'cms:draft:ensure', pageId: page.id });
      await loadPage(page.id);
      return true;
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to create draft.' });
      return false;
    } finally {
      setLoading(false);
    }
  }, [page, session, loadPage]);

  const openSectionEditor = useCallback(async (sectionId: string) => {
    setSelectedSectionId(sectionId);
    if (!draft) {
      const ready = await ensureDraft();
      if (!ready) return;
    }
    setSelectedSectionId(sectionId);
  }, [draft, ensureDraft]);

  const queuePreviewRefresh = useCallback(() => {
    pendingPreviewRefreshRef.current = true;
    if (previewRefreshTimerRef.current) {
      window.clearTimeout(previewRefreshTimerRef.current);
    }
    if (previewMode !== 'live') return;
    previewRefreshTimerRef.current = window.setTimeout(() => {
      refreshEmbeddedPreview().catch((error) => setStatus({ tone: 'warning', text: (error as Error).message || 'Preview may be behind; retry.' }));
    }, 500);
  }, [previewMode, refreshEmbeddedPreview]);

  const saveDraft = useCallback((options?: { reason?: 'manual' | 'autosave' | 'publish'; quiet?: boolean; draftOverride?: CmsVersion | null }) => {
    if (savePromiseRef.current) return savePromiseRef.current;
    const currentPage = pageRef.current;
    const currentDraft = options?.draftOverride ?? draftRef.current;
    if (!currentPage || !currentDraft) return Promise.resolve(false);

    const draftSnapshot = clone(currentDraft);
    const snapshotKey = stableStringify(draftSnapshot);
    const expectedUpdatedAt = draftUpdatedAtRef.current || baselineDraftRef.current?.updated_at;

    const runner = (async () => {
      setSaveBusy(true);
      setDraftSaveState('saving');
      if (!options?.quiet) setStatus(null);
      try {
        const result = await cmsFetch<CmsGetPageResponse>(session, {
          action: 'cms:draft:save',
          pageId: currentPage.id,
          versionId: draftSnapshot.id,
          title: draftSnapshot.title,
          seo: draftSnapshot.seo,
          sections: draftSnapshot.sections,
          pageData: draftSnapshot.page_data,
          expectedUpdatedAt,
        });
        const savedDraft = result.page.draft ?? null;
        setPage(result.page);
        setGlobalsValue((result.globals?.value ?? {}) as CmsGlobals);
        if (savedDraft) {
          setBaselineDraft(clone(savedDraft));
          setLastSavedAt(savedDraft.updated_at);
          draftUpdatedAtRef.current = savedDraft.updated_at;
          setDraft((current) => {
            if (!current) return current;
            return stableStringify(current) === snapshotKey ? clone(savedDraft) : current;
          });
          queuePreviewRefresh();
        }
        setDraftSaveState('saved');
        return true;
      } catch (error) {
        setDraftSaveState('error');
        setStatus({ tone: 'error', text: (error as Error).message || 'Failed to save draft.' });
        return false;
      } finally {
        setSaveBusy(false);
        savePromiseRef.current = null;
      }
    })();

    savePromiseRef.current = runner;
    return runner;
  }, [session, queuePreviewRefresh]);

  const flushDraftBeforePublish = useCallback(async () => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (savePromiseRef.current) {
      const activeSave = await savePromiseRef.current;
      if (!activeSave) return false;
    }
    const currentDraft = draftRef.current;
    const currentBaseline = baselineDraftRef.current;
    if (!currentDraft) return false;
    if (!currentBaseline || stableStringify(currentDraft) !== stableStringify(currentBaseline)) {
      return await saveDraft({ reason: 'publish', quiet: true, draftOverride: currentDraft });
    }
    return true;
  }, [saveDraft]);

  const publishPage = useCallback(async () => {
    const currentPage = page;
    const currentDraft = draftRef.current;
    if (!currentPage || !currentDraft) return;
    setPublishBusy(true);
    setDraftSaveState('saving');
    setStatus({ tone: 'info', text: 'Publishing…' });
    try {
      const ready = await flushDraftBeforePublish();
      if (!ready) return;
      const latestDraft = draftRef.current;
      const latestBaseline = baselineDraftRef.current;
      if (!latestDraft) return;
      await cmsFetch(session, {
        action: 'cms:publish',
        pageId: currentPage.id,
        versionId: latestDraft.id,
        title: latestDraft.title,
        seo: latestDraft.seo,
        sections: latestDraft.sections,
        pageData: latestDraft.page_data,
        expectedUpdatedAt: draftUpdatedAtRef.current || latestBaseline?.updated_at,
      });
      await loadPage(currentPage.id);
      await loadPages();
      setDraftSaveState('saved');
      setStatus({ tone: 'success', text: 'Published and next draft created.' });
    } catch (error) {
      setDraftSaveState('error');
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to publish.' });
    } finally {
      setPublishBusy(false);
    }
  }, [flushDraftBeforePublish, loadPage, loadPages, page, session]);

  useEffect(() => {
    if (!page || !draft || !dirty || loading || publishBusy) return undefined;
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      saveDraft({ reason: 'autosave', quiet: true }).catch((error) => {
        setStatus({ tone: 'error', text: (error as Error).message || 'Failed to save draft.' });
      });
    }, 850);
    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [page, draft, dirty, loading, publishBusy, saveDraft]);

  useEffect(() => {
    if (previewMode === 'live' && pendingPreviewRefreshRef.current) {
      queuePreviewRefresh();
    }
  }, [previewMode, queuePreviewRefresh]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    if (previewRefreshTimerRef.current) window.clearTimeout(previewRefreshTimerRef.current);
  }, []);

  const restoreRevision = useCallback(async (revisionId: string) => {
    if (!page || !draft) return;
    setLoading(true);
    setStatus({ tone: 'info', text: 'Restoring revision…' });
    try {
      await cmsFetch(session, {
        action: 'cms:revision:restore',
        pageId: page.id,
        revisionId,
        expectedUpdatedAt: baselineDraft?.updated_at,
      });
      await loadPage(page.id);
      setStatus({ tone: 'success', text: 'Revision restored into the current draft.' });
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to restore revision.' });
    } finally {
      setLoading(false);
    }
  }, [page, draft, session, baselineDraft, loadPage]);

  const openHostedPreview = useCallback(async () => {
    if (!page) return;
    if (dirtyRef.current) {
      const ready = await saveDraft({ reason: 'manual', quiet: true });
      if (!ready) return;
    }
    const resolvedPreviewUrl = previewUrl || await refreshEmbeddedPreview();
    const externalUrl = (resolvedPreviewUrl || '').replace(/([?&])embed=1(&|$)/, '$1').replace(/[?&]t=\d+/g, '').replace(/[?&]$/, '');
    if (externalUrl) window.open(externalUrl, '_blank', 'noopener,noreferrer');
  }, [page, previewUrl, refreshEmbeddedPreview, saveDraft]);

  const saveGlobals = useCallback(async () => {
    setSavingGlobals(true);
    try {
      await cmsFetch(session, { action: 'cms:globals:save', value: globalsValue });
      setStatus({ tone: 'success', text: 'Site settings saved.' });
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to save site settings.' });
    } finally {
      setSavingGlobals(false);
    }
  }, [session, globalsValue]);

  const createPage = useCallback(async () => {
    if (!newPageTitle.trim()) return;
    const slug = newPageSlug.trim() || slugify(newPageTitle);
    const route = newPageType === 'landing'
      ? `/lp/${slug}/`
      : (newPageRoute.trim() || `/${slugify(newPageTitle)}/`);
    setLoading(true);
    try {
      const result = await cmsFetch<CmsGetPageResponse>(session, {
        action: newPageSourceId ? 'cms:page:clone' : 'cms:page:create',
        title: newPageTitle.trim(),
        route,
        slug,
        pageType: newPageType,
        sourcePageId: newPageSourceId || undefined,
      });
      resetNewPageForm();
      await loadPages();
      setSelectedPageId(result.page.id);
      setLeftDrawerOpen(false);
      setStatus({ tone: 'success', text: `${route} ${newPageSourceId ? 'cloned' : 'created'}.` });
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to create page.' });
    } finally {
      setLoading(false);
    }
  }, [newPageSourceId, newPageTitle, newPageRoute, newPageSlug, newPageType, session, loadPages, resetNewPageForm]);

  const addSection = useCallback((template: SectionTemplate) => {
    const nextId = `${template.type}-${Date.now().toString(36)}`;
    updateDraft((current) => {
      const next = clone(template.defaultSection);
      next.id = nextId;
      return {
        ...current,
        sections: [...current.sections, next],
      };
    });
    setSelectedSectionId(nextId);
  }, [updateDraft]);

  const removeSection = useCallback((sectionId: string) => {
    updateDraft((current) => ({
      ...current,
      sections: current.sections.filter((section) => section.id !== sectionId),
    }));
    setSelectedSectionId('');
  }, [updateDraft]);

  const duplicateSection = useCallback((sectionId: string) => {
    updateDraft((current) => {
      const index = current.sections.findIndex((section) => section.id === sectionId);
      if (index < 0) return current;
      const copy = clone(current.sections[index]);
      copy.id = `${copy.type}-${Date.now().toString(36)}`;
      const nextSections = [...current.sections];
      nextSections.splice(index + 1, 0, copy);
      return { ...current, sections: nextSections };
    });
  }, [updateDraft]);

  const moveSection = useCallback((sectionId: string, direction: -1 | 1) => {
    updateDraft((current) => {
      const index = current.sections.findIndex((section) => section.id === sectionId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.sections.length) return current;
      const nextSections = [...current.sections];
      const [item] = nextSections.splice(index, 1);
      nextSections.splice(nextIndex, 0, item);
      return { ...current, sections: nextSections };
    });
  }, [updateDraft]);

  const handleMediaUpload = useCallback(async (file: File) => {
    const extension = file.name.includes('.') ? file.name.split('.').pop() : 'bin';
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${extension}`;
    setLoading(true);
    try {
      const upload = await supabase.storage.from('cms-media').upload(path, file, {
        contentType: file.type,
        upsert: true,
      });
      if (upload.error) throw upload.error;
      await cmsFetch(session, {
        action: 'cms:media:register',
        media: {
          bucket: 'cms-media',
          path,
          title: file.name.replace(/\.[^.]+$/, ''),
          alt_text: '',
          mime_type: file.type,
          size_bytes: file.size,
        },
      });
      await loadMedia();
      setStatus({ tone: 'success', text: `${file.name} uploaded.` });
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to upload media.' });
    } finally {
      setLoading(false);
    }
  }, [session, loadMedia]);

  const updateMediaDraftField = useCallback((assetId: string, field: keyof CmsMediaAsset, value: unknown) => {
    setMedia((current) => current.map((asset) => (
      asset.id === assetId ? { ...asset, [field]: value } : asset
    )));
  }, []);

  const saveMediaMetadata = useCallback(async (asset: CmsMediaAsset) => {
    setLoading(true);
    try {
      await cmsFetch(session, {
        action: 'cms:media:update',
        assetId: asset.id,
        media: {
          title: asset.title,
          alt_text: asset.alt_text,
          focal_x: asset.focal_x ?? 0,
          focal_y: asset.focal_y ?? 0,
          metadata: asset.metadata ?? {},
        },
      });
      await loadMedia();
      setStatus({ tone: 'success', text: 'Asset details saved.' });
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to save asset details.' });
    } finally {
      setLoading(false);
    }
  }, [session, loadMedia]);

  const deleteMedia = useCallback(async (assetId: string) => {
    setLoading(true);
    try {
      await cmsFetch(session, { action: 'cms:media:delete', assetId });
      await loadMedia();
      setStatus({ tone: 'success', text: 'Asset deleted.' });
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to delete asset.' });
    } finally {
      setLoading(false);
    }
  }, [session, loadMedia]);

  const filteredMedia = useMemo(() => {
    const query = mediaSearch.trim().toLowerCase();
    if (!query) return media;
    return media.filter((item) => `${item.title} ${item.path} ${item.alt_text}`.toLowerCase().includes(query));
  }, [media, mediaSearch]);

  const saveStateLabel = draftSaveState === 'saving'
    ? (publishBusy ? 'Publishing…' : 'Saving…')
    : draftSaveState === 'error'
      ? 'Save failed'
      : draftSaveState === 'unsaved'
        ? 'Unsaved'
        : 'Saved';

  const saveStateDetail = draftSaveState === 'saving'
    ? 'Changes are being written in the background.'
    : draftSaveState === 'error'
      ? 'Retry save or publish once the error is resolved.'
      : draftSaveState === 'unsaved'
        ? 'Autosave runs after a short pause.'
        : lastSavedAt
          ? `Last saved ${formatDate(lastSavedAt)}`
          : 'Ready to edit.';

  const previewStateDetail = previewSyncState === 'error'
    ? 'Preview may be behind; retry.'
    : previewSyncState === 'updating' || previewBusy
      ? 'Preview is updating…'
      : dirty
        ? 'Unsaved edits will appear after autosave.'
        : 'Saved draft is showing in preview.';

  return (
    <div className="admin-page cms-workspace">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">CMS</div>
          <div className="admin-subtitle">Manage the public `rvlryapp.com` pages with a preview-first workspace.</div>
        </div>
        <div className="admin-actions">
          <button className="admin-btn cms-mobile-workspace-toggle" type="button" onClick={() => setLeftDrawerOpen(true)}>
            Workspace
          </button>
          <button className="admin-btn" type="button" onClick={loadPages} disabled={loading || publishBusy}>Reload pages</button>
          <button className="admin-btn" type="button" onClick={loadMedia} disabled={loading || publishBusy}>Reload media</button>
          <button className="admin-btn" type="button" onClick={openHostedPreview} disabled={!page || previewBusy || publishBusy}>Hosted preview</button>
          <button className="admin-btn" type="button" onClick={() => { void saveDraft({ reason: 'manual' }); }} disabled={loading || publishBusy || !draft || (!dirty && !saveBusy)}>
            {saveBusy && !publishBusy ? 'Saving…' : 'Save draft'}
          </button>
          <button className="admin-btn primary" type="button" onClick={publishPage} disabled={loading || publishBusy || !draft}>
            {publishBusy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {shouldShowStatusBanner(status) ? statusPill(status) : null}

      <div className={clsx('cms-layout', leftDrawerOpen && 'left-open', canvasFocus && 'canvas-focus')}>
        <aside className="cms-drawer cms-drawer-left">
          <div className="cms-drawer-head">
            <div>
              <div className="admin-section-title">Workspace</div>
              <div className="admin-muted">Pages, site settings, and media live here.</div>
            </div>
            <button className="admin-btn small cms-drawer-close" type="button" onClick={() => setLeftDrawerOpen(false)}>
              Close
            </button>
          </div>
          <div className="cms-drawer-tabs">
            <button className={clsx('cms-drawer-tab', leftDrawerTab === 'pages' && 'active')} type="button" onClick={() => setLeftDrawerTab('pages')}>Pages</button>
            <button className={clsx('cms-drawer-tab', leftDrawerTab === 'settings' && 'active')} type="button" onClick={() => setLeftDrawerTab('settings')}>Site settings</button>
            <button className={clsx('cms-drawer-tab', leftDrawerTab === 'media' && 'active')} type="button" onClick={() => setLeftDrawerTab('media')}>Media</button>
          </div>

          {leftDrawerTab === 'pages' ? (
            <div className="cms-card-stack">
              <div className="cms-pane-head">
                <div>
                  <div className="admin-section-title">Pages</div>
                  <div className="admin-muted">{pages.length} routes</div>
                </div>
                <button
                  className="admin-btn small"
                  type="button"
                  onClick={() => {
                    if (newPageOpen) {
                      resetNewPageForm();
                    } else {
                      setNewPageSourceId('');
                      setNewPageType('standard');
                      setNewPageOpen(true);
                    }
                  }}
                >
                  {newPageOpen ? 'Close' : 'New page'}
                </button>
              </div>

              {newPageOpen ? (
                <div className="cms-card-stack cms-soft-card">
                  <label className="admin-label">
                    Page type
                    <select className="admin-select" value={newPageType} onChange={(e) => setNewPageType(e.target.value as CmsPageType)}>
                      <option value="standard">Standard</option>
                      <option value="landing">Landing</option>
                    </select>
                  </label>
                  <label className="admin-label">
                    Title
                    <input className="admin-input" value={newPageTitle} onChange={(e) => setNewPageTitle(e.target.value)} />
                  </label>
                  <label className="admin-label">
                    Route
                    <input
                      className="admin-input"
                      value={newPageType === 'landing' ? `/lp/${newPageSlug.trim() || slugify(newPageTitle) || 'new-page'}/` : newPageRoute}
                      onChange={(e) => setNewPageRoute(e.target.value)}
                      placeholder={newPageType === 'landing' ? '/lp/new-page/' : '/new-page/'}
                      disabled={newPageType === 'landing'}
                    />
                  </label>
                  <label className="admin-label">
                    Slug
                    <input className="admin-input" value={newPageSlug} onChange={(e) => setNewPageSlug(e.target.value)} placeholder="new-page" />
                  </label>
                  {newPageSourceId ? (
                    <div className="admin-help">Cloning from the selected page into a new {pageTypeLabel(newPageType).toLowerCase()} route.</div>
                  ) : null}
                  <button className="admin-btn primary" type="button" onClick={createPage} disabled={loading || !newPageTitle.trim()}>
                    {newPageSourceId ? 'Clone page' : 'Create page'}
                  </button>
                </div>
              ) : null}

              <div className="cms-page-list">
                {pages.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={clsx('cms-page-item', selectedPageId === item.id && 'active')}
                    onClick={() => {
                      setSelectedPageId(item.id);
                      setLeftDrawerOpen(false);
                    }}
                  >
                    <strong>{item.title}</strong>
                    <span>{item.route}</span>
                    <div className="cms-page-meta">
                      <span className="admin-badge">{pageTypeLabel(item.page_type)}</span>
                      {item.published ? <span className="admin-badge success">Published v{item.published.version_number}</span> : null}
                      {item.draft ? <span className="admin-badge override">Draft v{item.draft.version_number}</span> : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {leftDrawerTab === 'settings' ? (
            <div className="cms-card-stack">
              <div className="admin-section-title">Site settings</div>
              <div className="cms-soft-card cms-card-stack">
                <label className="admin-label">
                  Site name
                  <input
                    className="admin-input"
                    value={textValue(globalsValue.siteName)}
                    onChange={(e) => setGlobalsValue((current) => ({ ...current, siteName: e.target.value }))}
                  />
                </label>
                <label className="admin-label">
                  Support email
                  <input
                    className="admin-input"
                    value={textValue(globalsValue.supportEmail)}
                    onChange={(e) => setGlobalsValue((current) => ({ ...current, supportEmail: e.target.value }))}
                  />
                </label>
                <label className="admin-label">
                  Primary CTA label
                  <input
                    className="admin-input"
                    value={textValue(globalsValue.primaryCtaLabel)}
                    onChange={(e) => setGlobalsValue((current) => ({ ...current, primaryCtaLabel: e.target.value }))}
                  />
                </label>
                <label className="admin-label">
                  Primary CTA URL
                  <input
                    className="admin-input"
                    value={textValue(globalsValue.primaryCtaUrl)}
                    onChange={(e) => setGlobalsValue((current) => ({ ...current, primaryCtaUrl: e.target.value }))}
                  />
                </label>
                <button className="admin-btn" type="button" disabled={savingGlobals} onClick={saveGlobals}>
                  Save site settings
                </button>
              </div>
            </div>
          ) : null}

          {leftDrawerTab === 'media' ? (
            <div className="cms-card-stack">
              <div className="cms-pane-head">
                <div>
                  <div className="admin-section-title">Media library</div>
                  <div className="admin-muted">{media.length} assets</div>
                </div>
                <button className="admin-btn small" type="button" onClick={() => fileInputRef.current?.click()}>
                  Upload
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) handleMediaUpload(file).catch((error) => setStatus({ tone: 'error', text: (error as Error).message }));
                  event.target.value = '';
                }}
              />
              <label className="admin-label">
                Search assets
                <input className="admin-input" value={mediaSearch} onChange={(e) => setMediaSearch(e.target.value)} />
              </label>
              {assetTarget ? (
                <div className="admin-help">
                  Select an asset to apply it to `{assetTarget.field}`.
                </div>
              ) : null}
              <div className="cms-media-list">
                {filteredMedia.map((asset) => (
                  <div key={asset.id} className="cms-media-item">
                    <img src={asset.publicUrl} alt={asset.alt_text || asset.title} />
                    <div>
                      <strong>{asset.title || asset.path}</strong>
                      <span>{asset.path}</span>
                      <span>{asset.usageCount} use{asset.usageCount === 1 ? '' : 's'}</span>
                      <label className="admin-label" style={{ marginTop: 8 }}>
                        Title
                        <input
                          className="admin-input"
                          value={asset.title}
                          onChange={(e) => updateMediaDraftField(asset.id, 'title', e.target.value)}
                        />
                      </label>
                      <label className="admin-label">
                        Alt text
                        <input
                          className="admin-input"
                          value={asset.alt_text}
                          onChange={(e) => updateMediaDraftField(asset.id, 'alt_text', e.target.value)}
                        />
                      </label>
                    </div>
                    <div className="cms-media-actions">
                      <button
                        className="admin-btn small"
                        type="button"
                        onClick={() => {
                          if (!assetTarget || !draft) return;
                          if (assetTarget.kind === 'section') {
                            if (!selectedSection) return;
                            updateSelectedSection((section) => {
                              const nextProps = { ...(section.props ?? {}), [assetTarget.field]: asset.publicUrl };
                              if (assetTarget.altField && !textValue(nextProps[assetTarget.altField])) nextProps[assetTarget.altField] = asset.alt_text;
                              return { ...section, props: nextProps };
                            });
                          } else {
                            updateLandingData((current) => {
                              const next = { ...current, [assetTarget.field]: asset.publicUrl };
                              if (assetTarget.altField && !textValue(next[assetTarget.altField])) next[assetTarget.altField] = asset.alt_text;
                              return next;
                            });
                          }
                          setAssetTarget(null);
                          setLeftDrawerOpen(false);
                        }}
                        disabled={!assetTarget || !draft || (assetTarget.kind === 'section' && !selectedSection)}
                      >
                        Apply
                      </button>
                      <button className="admin-btn small" type="button" onClick={() => saveMediaMetadata(asset)}>
                        Save details
                      </button>
                      <button className="admin-btn small danger" type="button" onClick={() => deleteMedia(asset.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </aside>

        <aside className="cms-editor-pane">
          <div className="cms-editor-pane-head">
            <div className="cms-editor-pane-meta">
              <div className="admin-section-title">Editor</div>
              <div className="admin-muted">
                {selectedSection
                  ? `Editing ${findTemplate(selectedSection.type)?.label || selectedSection.type}`
                  : page
                    ? 'Select a section to edit.'
                    : 'Select a page to start editing.'}
              </div>
              <div className="cms-save-state-line">
                <span className={clsx('cms-save-pill', `is-${draftSaveState}`)}>{saveStateLabel}</span>
                <span className="cms-save-state-detail">{saveStateDetail}</span>
              </div>
            </div>
          </div>

          {page ? (
            <div className="cms-page-context cms-soft-card">
              <div className="cms-page-context-head">
                <div>
                  <div className="cms-editor-form-kicker">Current page</div>
                  <h3 className="cms-page-context-title">{draft?.title ?? page.title}</h3>
                </div>
                <div className="cms-page-context-route">{page.route}</div>
              </div>
              <div className="cms-page-context-meta">
                <span className="admin-badge">{pageTypeLabel(page.page_type)}</span>
                {page.published ? <span className="admin-badge success">Published v{page.published.version_number}</span> : null}
                {draft ? <span className="admin-badge override">Draft v{draft.version_number}</span> : null}
                <span className="admin-badge">{previewMode === 'live' ? 'Live preview' : 'Outline preview'}</span>
                <span className="admin-badge">{activeVersion?.sections.filter((section) => !section.hidden).length ?? 0} visible sections</span>
              </div>
              <div className="cms-page-context-actions">
                <a className="admin-btn" href={publicPageHref(page.route)} target="_blank" rel="noreferrer">
                  Open live page
                </a>
                <button
                  className="admin-btn"
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(publicPageHref(page.route));
                    setStatus({ tone: 'success', text: 'Page URL copied.' });
                  }}
                >
                  Copy URL
                </button>
                <button className="admin-btn" type="button" onClick={() => refreshEmbeddedPreview().catch((error) => setStatus({ tone: 'error', text: (error as Error).message }))}>
                  Refresh preview
                </button>
                <button
                  className="admin-btn"
                  type="button"
                  onClick={() => {
                    setNewPageTitle(`${draft?.title ?? page.title} Copy`);
                    setNewPageSlug(`${page.slug}-copy`);
                    setNewPageRoute(page.page_type === 'landing' ? '' : `/${page.slug}-copy/`);
                    setNewPageType(page.page_type === 'home' ? 'standard' : page.page_type);
                    setNewPageSourceId(page.id);
                    setNewPageOpen(true);
                    setLeftDrawerTab('pages');
                    setLeftDrawerOpen(true);
                  }}
                >
                  Clone page
                </button>
              </div>
              <div className="cms-editor-mode-tabs">
                <button className={clsx('admin-btn', editorMode === 'content' && 'primary')} type="button" onClick={() => setEditorMode('content')}>Content</button>
                <button className={clsx('admin-btn', editorMode === 'page' && 'primary')} type="button" onClick={() => setEditorMode('page')}>Page</button>
                <button className={clsx('admin-btn', editorMode === 'history' && 'primary')} type="button" onClick={() => setEditorMode('history')}>History</button>
              </div>
            </div>
          ) : null}

          {editorMode === 'content' && activeVersion ? (
            <div className="cms-editor-stack">
              <div className="cms-card-stack cms-soft-card cms-section-column">
                <div className="cms-pane-head">
                  <div>
                    <div className="admin-section-title">Sections</div>
                    <div className="admin-muted">{activeVersion.sections.length} blocks on this page</div>
                  </div>
                  {draft ? (
                    <div className="cms-section-actions">
                      {SECTION_LIBRARY.map((template) => (
                        <button key={template.type} className="admin-btn small" type="button" onClick={() => addSection(template)}>
                          + {template.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="admin-muted">Select a section to create a draft and edit it.</div>
                  )}
                </div>
                <label className="admin-label cms-section-search">
                  Search sections
                  <input
                    className="admin-input"
                    value={sectionSearch}
                    onChange={(e) => setSectionSearch(e.target.value)}
                    placeholder="Find hero, faq, story-home-2…"
                  />
                </label>
                <div className="cms-section-list">
                  {filteredSections.map((section) => {
                    const index = activeVersion.sections.findIndex((item) => item.id === section.id);
                    const summary = sectionSummary(section);
                    const locked = isLandingRequiredSection(page, section.id);
                    return (
                    <div key={section.id} className={clsx('cms-section-item', selectedSection?.id === section.id && 'active')}>
                      <button
                        type="button"
                        className="cms-section-select"
                        onClick={() => {
                          void openSectionEditor(section.id);
                        }}
                      >
                        <div className="cms-section-select-head">
                          <strong>{findTemplate(section.type)?.label || section.type}</strong>
                          <span className="cms-section-type-pill">{locked ? 'required' : section.type.replace(/_/g, ' ')}</span>
                        </div>
                        <span>{section.id}</span>
                        <span className="cms-section-summary-line">{summary.primary}</span>
                        <span className="cms-section-summary-line subtle">{summary.secondary}</span>
                      </button>
                      {draft ? (
                        <div className="cms-section-controls">
                          <button className="admin-btn small ghost" type="button" onClick={() => moveSection(section.id, -1)} disabled={index === 0}>↑</button>
                          <button className="admin-btn small ghost" type="button" onClick={() => moveSection(section.id, 1)} disabled={index === activeVersion.sections.length - 1}>↓</button>
                          <button className="admin-btn small ghost" type="button" onClick={() => duplicateSection(section.id)}>Duplicate</button>
                          <button className="admin-btn small ghost danger" type="button" onClick={() => removeSection(section.id)} disabled={locked}>Delete</button>
                        </div>
                      ) : (
                        <div className="cms-section-controls">
                          <button className="admin-btn small" type="button" onClick={() => { void openSectionEditor(section.id); }}>
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              {selectedSection ? (
                <div className="cms-card-stack cms-soft-card cms-editor-column">
                  <div className="cms-pane-head">
                    <div>
                      <div className="admin-section-title">
                        {findTemplate(selectedSection.type)?.label || selectedSection.type}
                      </div>
                      <div className="admin-muted">{selectedSection.id}</div>
                    </div>
                    <span className="cms-section-type-pill">
                      {sectionSummary(selectedSection).primary}
                    </span>
                  </div>
                  <SectionEditor
                    section={selectedSection}
                    updateProps={(nextProps) => updateSelectedSection((section) => ({ ...section, props: nextProps }))}
                    setHidden={(next) => updateSelectedSection((section) => ({ ...section, hidden: next }))}
                    pickMedia={(field, altField) => {
                      setAssetTarget({ kind: 'section', field, altField });
                      setLeftDrawerTab('media');
                      setLeftDrawerOpen(true);
                    }}
                  />
                </div>
              ) : (
                <div className="cms-soft-card cms-empty-editor">
                  <strong>Select a section</strong>
                  <span>Pick a section from the list above to edit its copy, media, and links.</span>
                </div>
              )}
            </div>
          ) : null}

          {editorMode === 'page' && page ? (
            <div className="cms-page-mode-grid">
              <div className="cms-soft-card cms-card-stack cms-editor-details">
                <div className="cms-pane-head">
                  <div>
                    <div className="admin-section-title">Page settings</div>
                    <div className="admin-muted">Search metadata and route-level configuration.</div>
                  </div>
                </div>
                <label className="admin-label">
                  Page title
                  <input
                    className="admin-input"
                    value={draft?.title ?? page.title}
                    onChange={(e) => draft && setDraft({ ...draft, title: e.target.value })}
                    disabled={!draft}
                  />
                </label>
                <label className="admin-label">
                  SEO title
                  <input
                    className="admin-input"
                    value={textValue(draft?.seo.title)}
                    onChange={(e) => draft && setDraft({ ...draft, seo: { ...draft.seo, title: e.target.value } })}
                    disabled={!draft}
                  />
                </label>
                <label className="admin-label">
                  SEO description
                  <textarea
                    className="admin-input"
                    rows={4}
                    value={textValue(draft?.seo.description)}
                    onChange={(e) => draft && setDraft({ ...draft, seo: { ...draft.seo, description: e.target.value } })}
                    disabled={!draft}
                  />
                </label>
                <label className="admin-label">
                  Canonical URL
                  <input
                    className="admin-input"
                    value={textValue(draft?.seo.canonicalUrl)}
                    onChange={(e) => draft && setDraft({ ...draft, seo: { ...draft.seo, canonicalUrl: e.target.value } })}
                    disabled={!draft}
                  />
                </label>
                <label className="admin-label">
                  OG image URL
                  <input
                    className="admin-input"
                    value={textValue(draft?.seo.ogImageUrl)}
                    onChange={(e) => draft && setDraft({ ...draft, seo: { ...draft.seo, ogImageUrl: e.target.value } })}
                    disabled={!draft}
                  />
                </label>
                <label className="admin-label">
                  Noindex
                  <input
                    type="checkbox"
                    checked={Boolean(draft?.seo.noindex)}
                    onChange={(e) => draft && setDraft({ ...draft, seo: { ...draft.seo, noindex: e.target.checked } })}
                    disabled={!draft}
                  />
                </label>
              </div>

              <div className="cms-soft-card cms-card-stack cms-editor-details">
                <div className="cms-pane-head">
                  <div>
                    <div className="admin-section-title">Publishing</div>
                    <div className="admin-muted">Control draft creation, publishing, and how this page is reviewed.</div>
                  </div>
                </div>
                <label className="admin-label">
                  Public route
                  <input className="admin-input" value={page.route} disabled />
                </label>
                <label className="admin-label">
                  Public URL
                  <div className="cms-inline-field">
                    <input className="admin-input" value={publicPageHref(page.route)} disabled />
                    <button
                      className="admin-btn"
                      type="button"
                      onClick={async () => {
                        await navigator.clipboard.writeText(publicPageHref(page.route));
                        setStatus({ tone: 'success', text: 'Page URL copied.' });
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </label>
                <div className="cms-page-publishing-actions">
                  {!draft && page.published ? (
                    <button className="admin-btn primary" type="button" onClick={ensureDraft} disabled={loading}>
                      Start draft from published
                    </button>
                  ) : null}
                  <a className="admin-btn" href={publicPageHref(page.route)} target="_blank" rel="noreferrer">
                    Open live page
                  </a>
                </div>
              </div>

              {page.page_type === 'landing' && draft ? (
                <div className="cms-soft-card cms-card-stack cms-editor-details">
                  <div className="cms-pane-head">
                    <div>
                      <div className="admin-section-title">Landing strategy</div>
                      <div className="admin-muted">Persona-specific theme, creative, and CTA configuration.</div>
                    </div>
                  </div>
                  <label className="admin-label">
                    Slug
                    <input className="admin-input" value={page.slug} disabled />
                  </label>
                  <label className="admin-label">
                    Audience label
                    <input
                      className="admin-input"
                      value={textValue(landingData(draft).audienceLabel)}
                      onChange={(e) => updateLandingData((current) => ({ ...current, audienceLabel: e.target.value }))}
                    />
                  </label>
                  <label className="admin-label">
                    Theme
                    <select
                      className="admin-select"
                      value={textValue(landingData(draft).themeKey) || 'street'}
                      onChange={(e) => updateLandingData((current) => ({ ...current, themeKey: e.target.value }))}
                    >
                      <option value="street">Street</option>
                      <option value="court">Court</option>
                      <option value="social">Social</option>
                      <option value="neutral">Neutral</option>
                    </select>
                  </label>
                  <label className="admin-label">
                    Hero mode
                    <select
                      className="admin-select"
                      value={textValue(landingData(draft).heroMode) || 'scene'}
                      onChange={(e) => updateLandingData((current) => ({ ...current, heroMode: e.target.value }))}
                    >
                      <option value="scene">Managed scene</option>
                      <option value="upload">Uploaded asset</option>
                    </select>
                  </label>
                  <label className="admin-label">
                    Hero scene
                    <select
                      className="admin-select"
                      value={textValue(landingData(draft).heroSceneOutputId) || 'rivals-community-desktop'}
                      onChange={(e) => updateLandingData((current) => ({ ...current, heroSceneOutputId: e.target.value }))}
                    >
                      {LANDING_HERO_SCENE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-label">
                    Hero image URL
                    <div className="cms-inline-field">
                      <input
                        className="admin-input"
                        value={textValue(landingData(draft).heroImageUrl)}
                        onChange={(e) => updateLandingData((current) => ({ ...current, heroImageUrl: e.target.value }))}
                      />
                      <button
                        className="admin-btn"
                        type="button"
                        onClick={() => {
                          setAssetTarget({ kind: 'landing', field: 'heroImageUrl', altField: 'heroImageAlt' });
                          setLeftDrawerTab('media');
                          setLeftDrawerOpen(true);
                        }}
                      >
                        Pick
                      </button>
                    </div>
                  </label>
                  <label className="admin-label">
                    Hero image alt
                    <input
                      className="admin-input"
                      value={textValue(landingData(draft).heroImageAlt)}
                      onChange={(e) => updateLandingData((current) => ({ ...current, heroImageAlt: e.target.value }))}
                    />
                  </label>
                  <label className="admin-label">
                    OG image URL
                    <div className="cms-inline-field">
                      <input
                        className="admin-input"
                        value={textValue(landingData(draft).ogImageUrl)}
                        onChange={(e) => {
                          updateLandingData((current) => ({ ...current, ogImageUrl: e.target.value }));
                          setDraft({ ...draft, seo: { ...draft.seo, ogImageUrl: e.target.value } });
                        }}
                      />
                      <button
                        className="admin-btn"
                        type="button"
                        onClick={() => {
                          setAssetTarget({ kind: 'landing', field: 'ogImageUrl' });
                          setLeftDrawerTab('media');
                          setLeftDrawerOpen(true);
                        }}
                      >
                        Pick
                      </button>
                    </div>
                  </label>
                  <label className="admin-label">
                    Sticky CTA label
                    <input
                      className="admin-input"
                      value={textValue(landingData(draft).stickyCtaLabel)}
                      onChange={(e) => updateLandingData((current) => ({ ...current, stickyCtaLabel: e.target.value }))}
                    />
                  </label>
                </div>
              ) : null}

              {page.route === '/join/' && draft ? (
                <div className="cms-soft-card cms-card-stack cms-editor-details">
                  <div className="cms-pane-head">
                    <div>
                      <div className="admin-section-title">Join shell</div>
                      <div className="admin-muted">Landing copy around the invite deep link flow.</div>
                    </div>
                  </div>
                  {['eyebrow', 'title', 'body', 'openLabel', 'browserLabel', 'downloadLabel', 'helpText'].map((key) => (
                    <label className="admin-label" key={key}>
                      {key}
                      <input
                        className="admin-input"
                        value={textValue((draft.page_data.joinShell as Record<string, unknown> | undefined)?.[key])}
                        onChange={(e) => {
                          const joinShell = clone((draft.page_data.joinShell as Record<string, unknown>) ?? {});
                          joinShell[key] = e.target.value;
                          setDraft({ ...draft, page_data: { ...draft.page_data, joinShell } });
                        }}
                      />
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {editorMode === 'history' ? (
            <div className="cms-soft-card cms-editor-details">
              <div className="cms-pane-head">
                <div>
                  <div className="admin-section-title">Revision history</div>
                  <div className="admin-muted">Restore a prior version or review the publishing trail.</div>
                </div>
              </div>
              <div className="cms-revision-list">
                {revisions.map((revision) => (
                  <div className="cms-revision-item" key={revision.id}>
                    <div>
                      <strong>v{revision.version_number}</strong>
                      <div className="admin-muted">{revision.status} · updated {formatDate(revision.updated_at)}</div>
                    </div>
                    {draft ? (
                      <button className="admin-btn small ghost" type="button" onClick={() => restoreRevision(revision.id)}>
                        Restore
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </aside>

        <section className="cms-preview-pane">
          <div className="cms-preview-pane-head">
            <div>
              <div className="admin-section-title">Canvas preview</div>
              <div className="admin-muted">
                {page ? `${page.route} · ${draft ? `Draft updated ${formatDate(draft.updated_at)}` : `Published updated ${formatDate(page.published?.updated_at)}`}` : 'Select a page'}
              </div>
              <div className="cms-preview-status">{previewStateDetail}</div>
            </div>
            <div className="cms-stage-header-actions">
              {selectedSection ? <span className="admin-badge override">Editing {findTemplate(selectedSection.type)?.label || selectedSection.type}</span> : null}
              <button className="admin-btn" type="button" onClick={() => setCanvasFocus((value) => !value)}>
                {canvasFocus ? 'Show editor' : 'Focus canvas'}
              </button>
            </div>
          </div>

          <div className="cms-stage">
            <div className="cms-stage-toolbar">
              <div className="cms-stage-toolbar-group">
                <div className="admin-segmented">
                  <button className={clsx('admin-seg', previewMode === 'live' && 'active')} type="button" onClick={() => setPreviewMode('live')}>
                    Live preview
                  </button>
                  <button className={clsx('admin-seg', previewMode === 'outline' && 'active')} type="button" onClick={() => setPreviewMode('outline')}>
                    Outline
                  </button>
                </div>
                <div className="admin-segmented">
                  <button className={clsx('admin-seg', viewport === 'desktop' && 'active')} type="button" onClick={() => setViewport('desktop')}>
                    Desktop
                  </button>
                  <button className={clsx('admin-seg', viewport === 'tablet' && 'active')} type="button" onClick={() => setViewport('tablet')}>
                    Tablet
                  </button>
                  <button className={clsx('admin-seg', viewport === 'mobile' && 'active')} type="button" onClick={() => setViewport('mobile')}>
                    Mobile
                  </button>
                </div>
              </div>
              <div className="cms-stage-toolbar-group">
                {heroSection ? (
                  <button
                    className="admin-btn"
                    type="button"
                    onClick={() => {
                      void openSectionEditor(heroSection.id);
                    }}
                  >
                    Edit hero
                  </button>
                ) : null}
                {!draft && page?.published ? (
                  <button className="admin-btn" type="button" onClick={ensureDraft} disabled={loading}>
                    Start draft from published
                  </button>
                ) : null}
              </div>
            </div>

            <div className="cms-stage-canvas">
              {previewMode === 'live' ? (
                <LivePagePreview previewUrl={previewUrl} viewport={viewport} previewBusy={previewBusy} page={page} statusText={previewStateDetail} onRefresh={() => refreshEmbeddedPreview().catch((error) => setStatus({ tone: 'error', text: (error as Error).message }))} />
              ) : (
                <OutlinePagePreview page={page ? { ...page, draft: draft ?? page.draft ?? null } : null} globals={globalsValue} />
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
